import React from "react";
import { Plus, X } from "lucide-react";
import { INDICATORS, findIndicator } from "@/lib/indicators";
import { Select } from "./BuilderPrimitives";


/** Card that holds a list of entry/exit rules with add + hint. */
export function RulesPanel({ title, hint, rules, onAdd, onRemove, onUpdate, testIdPrefix, accent }) {
  const accentClass = accent === "profit"
    ? "hover:!text-[var(--profit)] hover:!border-[var(--profit)]/60"
    : "hover:!text-[var(--loss)] hover:!border-[var(--loss)]/60";
  return (
    <section className="card-sharp p-5">
      <div className="flex items-start justify-between mb-1">
        <h2 className="font-display text-lg text-white">{title}</h2>
        <button data-testid={`add-${testIdPrefix}-rule-btn`} onClick={onAdd}
          className={`btn-outline flex items-center gap-1.5 text-xs !py-1.5 ${accentClass}`}>
          <Plus size={12}/> Add rule
        </button>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-4">{hint}</p>
      {rules.length === 0 && (
        <div className="text-xs font-mono text-[var(--text-muted)] italic border border-dashed border-white/10 rounded p-4"
          data-testid={`${testIdPrefix}-empty`}>
          No rules yet. Click "Add rule" to compose a condition.
        </div>
      )}
      <div className="space-y-2">
        {rules.map((r, i) => (
          <RuleRow key={r._rid || `rule-${i}`} idx={i} rule={r} testIdPrefix={testIdPrefix}
            onRemove={()=>onRemove(i)}
            onUpdate={(p)=>onUpdate(i, p)}/>
        ))}
      </div>
    </section>
  );
}


function RuleRow({ rule, idx, onRemove, onUpdate, testIdPrefix }) {
  const ind = findIndicator(rule.indicator);
  const isCross = String(rule.operator).startsWith("cross_");
  const wantsRef = ind.valueMode === "price" || isCross;
  const [showScope, setShowScope] = React.useState(!!(rule.symbol_override || rule.timeframe_override));
  return (
    <div className="border border-white/10 rounded p-3" data-testid={`${testIdPrefix}-rule-${idx}`}>
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-28">
          <label className="label-tiny">Side</label>
          <Select data-testid={`${testIdPrefix}-rule-${idx}-direction`}
            value={rule.direction || "buy"} onChange={(v)=>onUpdate({direction:v})}
            options={[{v:"buy",l:"BUY"},{v:"sell",l:"SELL"}]}/>
        </div>
        <div className="w-44">
          <label className="label-tiny">Indicator</label>
          <Select data-testid={`${testIdPrefix}-rule-${idx}-indicator`}
            value={rule.indicator} onChange={(v)=>onUpdate({indicator:v})}
            options={INDICATORS.map(x=>({v:x.id,l:x.label}))}/>
        </div>
        {ind.fields.map((f) => (
          <div key={f.name} className="w-28">
            <label className="label-tiny">{f.label}</label>
            {f.type === "select" ? (
              <Select data-testid={`${testIdPrefix}-rule-${idx}-${f.name}`}
                value={rule[f.name] ?? f.default}
                onChange={(v)=>onUpdate({[f.name]:v})}
                options={f.options.map(o=>({v:o,l:o.replace("MODE_","")}))}/>
            ) : (
              <input data-testid={`${testIdPrefix}-rule-${idx}-${f.name}`}
                type="number" className="input-dark"
                value={rule[f.name] ?? f.default}
                onChange={(e)=>onUpdate({[f.name]: parseFloat(e.target.value) || 0})}/>
            )}
          </div>
        ))}
        <div className="w-36">
          <label className="label-tiny">Operator</label>
          <Select data-testid={`${testIdPrefix}-rule-${idx}-operator`}
            value={rule.operator} onChange={(v)=>onUpdate({operator:v})}
            options={ind.ops.map(o=>({v:o, l:o.toUpperCase().replace(/_/g," ")}))}/>
        </div>
        {wantsRef ? (
          <div className="w-32">
            <label className="label-tiny">Vs</label>
            <Select data-testid={`${testIdPrefix}-rule-${idx}-reference`}
              value={rule.reference || "price"}
              onChange={(v)=>onUpdate({reference:v})}
              options={[{v:"price",l:"Price"}]}/>
          </div>
        ) : (
          <div className="w-24">
            <label className="label-tiny">Value</label>
            <input data-testid={`${testIdPrefix}-rule-${idx}-value`} type="number" className="input-dark"
              value={rule.value ?? 0}
              onChange={(e)=>onUpdate({value: parseFloat(e.target.value)||0})}/>
          </div>
        )}
        <button data-testid={`${testIdPrefix}-rule-${idx}-scope-toggle`}
          onClick={()=>setShowScope(s => !s)}
          className={`p-2 border rounded transition-colors ${(rule.symbol_override || rule.timeframe_override)
            ? "border-[var(--accent)]/60 !text-[var(--accent)]"
            : "border-white/10 hover:border-[var(--accent)]/40"}`}
          title="Multi-symbol / multi-timeframe">
          <span className="text-[10px] font-mono px-1">MTF</span>
        </button>
        <button data-testid={`${testIdPrefix}-rule-${idx}-remove`} onClick={onRemove}
          className="ml-auto p-2 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)] transition-colors">
          <X size={14}/>
        </button>
      </div>
      {showScope && (
        <ScopeOverrides rule={rule} onUpdate={onUpdate} testIdPrefix={testIdPrefix} idx={idx}/>
      )}
    </div>
  );
}


function ScopeOverrides({ rule, onUpdate, testIdPrefix, idx }) {
  return (
    <div className="flex gap-3 mt-3 pt-3 border-t border-white/5" data-testid={`${testIdPrefix}-rule-${idx}-scope-panel`}>
      <div className="w-40">
        <label className="label-tiny">Symbol override</label>
        <input data-testid={`${testIdPrefix}-rule-${idx}-symbol-override`}
          className="input-dark" placeholder="(chart symbol)"
          value={rule.symbol_override || ""}
          onChange={(e)=>onUpdate({symbol_override: e.target.value.toUpperCase()})}/>
      </div>
      <div className="w-40">
        <label className="label-tiny">Timeframe override</label>
        <select data-testid={`${testIdPrefix}-rule-${idx}-timeframe-override`}
          className="input-dark"
          value={rule.timeframe_override || ""}
          onChange={(e)=>onUpdate({timeframe_override: e.target.value})}>
          <option value="">(chart timeframe)</option>
          {["PERIOD_M1","PERIOD_M5","PERIOD_M15","PERIOD_M30","PERIOD_H1","PERIOD_H4","PERIOD_D1","PERIOD_W1"].map(tf =>
            <option key={tf} value={tf}>{tf.replace("PERIOD_","")}</option>
          )}
        </select>
      </div>
      <p className="flex-1 text-[10px] font-mono text-[var(--text-muted)] italic self-end pb-2">
        Reads this indicator from a different chart. Leave blank to use the EA's chart.
      </p>
    </div>
  );
}
